import { useEffect, useRef } from 'react';
import { sayable, type ActivityQuestion, type ReadAloud } from '@qq/shared';

/**
 * Read-aloud, with the iPad's own voice.
 *
 * The ONLY place in the app that talks to speechSynthesis (a test holds it to
 * that). What is said is decided on the server, per engine, so a word the
 * child is learning to read is never spoken; this file only says it.
 */
export type Speakable = NonNullable<ActivityQuestion['speak']>;
export { sayable };

function synth(): SpeechSynthesis | undefined {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
    ? window.speechSynthesis
    : undefined;
}

export function canSpeak(): boolean {
  return synth() !== undefined && typeof SpeechSynthesisUtterance !== 'undefined';
}

/** Picked lazily: iOS fills the voice list after the page loads. */
function voice(): SpeechSynthesisVoice | undefined {
  const voices = synth()?.getVoices() ?? [];
  return (
    voices.find((v) => v.lang.replace('_', '-') === 'en-AU') ??
    voices.find((v) => v.lang.toLowerCase().startsWith('en'))
  );
}

export function cancelSpeech(): void {
  const s = synth();
  if (s && (s.speaking || s.pending)) s.cancel();
}

export function sayAloud(speak: Speakable, onStart?: () => void): void {
  const s = synth();
  if (!s || !canSpeak()) return;
  // One voice at a time: a second tap restarts, it never talks over itself.
  cancelSpeech();
  const utterance = new SpeechSynthesisUtterance(sayable(speak));
  utterance.lang = 'en-AU';
  const v = voice();
  if (v) utterance.voice = v;
  utterance.rate = 0.9;
  if (onStart) utterance.onstart = onStart;
  s.speak(utterance);
}

/**
 * iOS will not speak until a speak() has happened inside a tap. The PIN pad
 * is taps, so the first one anywhere unlocks it, and a Prep child hears the
 * first question without having to tap again.
 */
export function primeSpeechOnFirstTap(): void {
  if (!canSpeak()) return;
  const prime = () => {
    const silent = new SpeechSynthesisUtterance(' ');
    silent.volume = 0;
    synth()?.speak(silent);
  };
  document.addEventListener('pointerdown', prime, { once: true, capture: true });
}

/**
 * Says each question once, as it appears, when the child's setting is 'auto'.
 *
 * Never while an answer is being checked or the feedback is showing, and the
 * voice stops the moment the question changes, the child answers, or the iPad
 * is put down. A question counts as said once speech has actually STARTED, so
 * a retry does not repeat it, and coming back to the app does not replay it.
 */
export function useReadAloud(
  questionId: string | undefined,
  speak: Speakable | undefined,
  mode: ReadAloud,
  asking: boolean,
): void {
  const spokenFor = useRef<string | null>(null);

  useEffect(() => {
    if (!questionId || !speak || mode !== 'auto' || !asking) return;
    if (spokenFor.current === questionId) return;
    sayAloud(speak, () => {
      spokenFor.current = questionId;
    });
    // `speak` belongs to the question; it changes only when questionId does.
  }, [questionId, mode, asking]);

  // A new question, an answer tapped, or leaving the screen: stop talking.
  useEffect(() => () => cancelSpeech(), [questionId, asking]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') cancelSpeech();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, []);
}
